"use client"

import { DestinationKindBadge } from "@/components/admin/onwards/destination-badges"
import { Table, TableCard } from "@/components/ui/table"

export interface ReachRow {
  id: string
  name: string
  kind: string
  status: string | null
  impressions: number
  referrals: number
  uptake_pct: number
  channels: Record<string, number>
}

interface ReachTableProps {
  rows: ReachRow[]
  "aria-label"?: string
  footer?: React.ReactNode
}

const channelSummary = (channels: Record<string, number>) =>
  Object.entries(channels)
    .map(([channel, count]) => `${count} ${channel}`)
    .join(" · ")

// The funnel table: one row per destination that has ever been surfaced.
// Read-only — the governance actions live on the destinations catalogue.
export function ReachTable({
  rows,
  "aria-label": ariaLabel = "Reach & referrals",
  footer,
}: ReachTableProps) {
  return (
    <TableCard.Root>
      <Table aria-label={ariaLabel} className="bg-primary">
        <Table.Header>
          <Table.Head
            id="name"
            isRowHeader
            label="Destination"
            className="w-full"
          />
          <Table.Head
            id="impressions"
            label="Impressions"
            className="text-right"
          />
          <Table.Head id="referrals" label="Referrals" className="text-right" />
          <Table.Head id="uptake" label="Uptake" className="text-right" />
          <Table.Head id="channels" label="By channel" />
        </Table.Header>
        <Table.Body items={rows}>
          {(row: ReachRow) => (
            <Table.Row id={row.id}>
              <Table.Cell>
                <div className="flex items-center gap-3">
                  <span className="text-primary text-sm font-medium">
                    {row.name}
                  </span>
                  <DestinationKindBadge kind={row.kind} />
                </div>
              </Table.Cell>
              <Table.Cell className="text-secondary text-right tabular-nums">
                {row.impressions}
              </Table.Cell>
              <Table.Cell className="text-secondary text-right tabular-nums">
                {row.referrals}
              </Table.Cell>
              <Table.Cell className="text-secondary text-right tabular-nums">
                {row.uptake_pct}%
              </Table.Cell>
              <Table.Cell className="text-tertiary text-xs">
                {channelSummary(row.channels) || "—"}
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
      {footer}
    </TableCard.Root>
  )
}
